// The settings registry (spec/03-architecture.md §9).
//
// One registry for the whole product. Each feature module declares its own settings with
// `defineSetting` and adds them here when it loads; nothing in this package populates it, because
// a central list of every setting is a file every pull request would have to touch and every merge
// would have to reconcile.
//
// The consoles' settings screens are generated from what is registered — type, description,
// permission — so a new setting needs no new screen.
import { z } from "../zod.js";
import { SettingDefinitionError, } from "./define-setting.js";
export class SettingsRegistry {
    #definitions = new Map();
    /**
     * Add settings. A key registered twice is an error rather than a silent overwrite: two modules
     * that both think they own `sessions.offlinePolicy` is a bug that must surface at boot, not the
     * day one of them changes its default.
     */
    register(...definitions) {
        for (const definition of definitions) {
            const existing = this.#definitions.get(definition.key);
            if (existing !== undefined && existing !== definition) {
                throw new SettingDefinitionError(`Setting "${definition.key}" is already registered by another module.`);
            }
            this.#definitions.set(definition.key, definition);
        }
        return this;
    }
    /** Add every setting in a module built with `defineSettings`. */
    registerModule(module) {
        return this.register(...Object.values(module));
    }
    get(key) {
        return this.#definitions.get(key);
    }
    has(key) {
        return this.#definitions.has(key);
    }
    /** Every setting, sorted by key so a generated screen and a generated document are stable. */
    list() {
        return [...this.#definitions.values()].sort((a, b) => a.key.localeCompare(b.key));
    }
    keys() {
        return this.list().map((definition) => definition.key);
    }
    get size() {
        return this.#definitions.size;
    }
    /** What is visible on one console's settings screen. */
    visibleTo(audience) {
        return this.list().filter((definition) => definition.visibleTo === audience);
    }
    /** The machine-readable form the consoles generate their screens from. */
    describe() {
        return this.list().map(describeSetting);
    }
    /** For tests, which must not inherit whatever another test registered. */
    clear() {
        this.#definitions.clear();
    }
}
export function describeSetting(definition) {
    return {
        key: definition.key,
        schema: z.toJSONSchema(definition.type, { io: 'input', unrepresentable: 'any' }),
        default: definition.default,
        scopes: definition.scopes,
        visibleTo: definition.visibleTo,
        group: definition.group,
        lockableAt: definition.lockableAt,
        editPermission: definition.editPermission,
        entitlement: definition.entitlement,
        dated: definition.dated === true,
        ...(definition.unit === undefined ? {} : { unit: definition.unit }),
        description: definition.description,
    };
}
/** The one registry. A module adds to it; nothing reads it before every module has loaded. */
export const settingsRegistry = new SettingsRegistry();
//# sourceMappingURL=registry.js.map